// src/components/BulkInvitationModal.jsx
import React, { useState, useEffect } from 'react';

/**
 * BulkInvitationModal Component
 * 
 * Displays after a new player is created with three options:
 * 1. Send All Upcoming Tee Times
 * 2. Select Specific Tee Times
 * 3. Skip (add no tee times)
 */
const BulkInvitationModal = ({ 
  isOpen, 
  onClose, 
  newPlayer,
  groupId 
}) => {
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // 'success' | 'error' | null
  const [upcomingTeeTimes, setUpcomingTeeTimes] = useState([]);
  const [isLoadingTeeTimes, setIsLoadingTeeTimes] = useState(true);
  const [showSelection, setShowSelection] = useState(false);
  const [selectedTeeTimeIds, setSelectedTeeTimeIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchUpcomingTeeTimes();
    }
  }, [isOpen]);

  const fetchUpcomingTeeTimes = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_URL}/teetimes`);
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        console.error('❌ API did not return an array:', data);
        setUpcomingTeeTimes([]);
        return;
      }
      
      // Get today's date
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayDateStr = `${year}-${month}-${day}`;
      
      // Filter for upcoming published tee times
      const upcoming = data.filter(tt => {
        const ttDateStr = tt.date.includes('T') ? tt.date.split('T')[0] : tt.date;
        const isUpcoming = ttDateStr >= todayDateStr;
        const isPublished = tt.status?.toLowerCase() === 'published' || !tt.status;
        
        // Debug logging
        if (!isUpcoming) {
          console.log(`❌ Filtered out (past): ${tt.course} on ${tt.date}`);
        }
        if (!isPublished) {
          console.log(`❌ Filtered out (status="${tt.status}"): ${tt.course} on ${tt.date}`);
        }
        
        return isUpcoming && isPublished;
      }).sort((a, b) => {
        // Sort by date, then time
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.time.localeCompare(b.time);
      });
      
      console.log(`✅ Found ${upcoming.length} upcoming published tee times:`, upcoming.map(tt => `${tt.course} - ${tt.date}`));
      setUpcomingTeeTimes(upcoming);
    } catch (error) {
      console.error('❌ Error fetching tee times:', error);
      setUpcomingTeeTimes([]);
    } finally {
      setIsLoadingTeeTimes(false);
    }
  };

  if (!isOpen || !newPlayer) return null;

  const handleSendAll = async () => {
    await sendTeeTimesToPlayer(upcomingTeeTimes.map(tt => tt.id));
  };

  const handleSelectTeeTimes = () => {
    setShowSelection(true);
  };

  const handleSendSelected = async () => {
    if (selectedTeeTimeIds.length === 0) {
      return;
    }
    await sendTeeTimesToPlayer(selectedTeeTimeIds);
  };

  const sendTeeTimesToPlayer = async (teeTimeIds) => {
    setIsSending(true);
    setSendStatus(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_URL}/players/send-tee-times`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          groupId: groupId,
          playerId: newPlayer.id,
          playerEmail: newPlayer.email,
          playerName: newPlayer.name,
          teeTimeIds: teeTimeIds // Pass specific tee time IDs
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send tee times');
      }

      setSendStatus('success');
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('❌ Error sending tee times:', error);
      setSendStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const toggleTeeTimeSelection = (teeTimeId) => {
    setSelectedTeeTimeIds(prev => 
      prev.includes(teeTimeId)
        ? prev.filter(id => id !== teeTimeId)
        : [...prev, teeTimeId]
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Selection view
  if (showSelection) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
        onClick={handleSkip}
      >
        <div 
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '24px', 
              fontWeight: 'bold',
              color: '#1f2937'
            }}>
              Select Tee Times
            </h2>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Choose which tee times to send to {newPlayer.name}
            </p>
          </div>

          {/* Tee Time List */}
          <div style={{ marginBottom: '24px' }}>
            {upcomingTeeTimes.map(tt => (
              <label
                key={tt.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  marginBottom: '8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: selectedTeeTimeIds.includes(tt.id) ? '#f0fdf4' : 'white',
                  borderColor: selectedTeeTimeIds.includes(tt.id) ? '#10b981' : '#e5e7eb',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedTeeTimeIds.includes(tt.id)}
                  onChange={() => toggleTeeTimeSelection(tt.id)}
                  style={{ 
                    marginRight: '12px', 
                    width: '18px', 
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                    {tt.course}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    {formatDate(tt.date)} at {formatTime(tt.time)} • {tt.location}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* Status Messages */}
          {sendStatus === 'success' && (
            <div style={{
              backgroundColor: '#d1fae5',
              border: '1px solid #10b981',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
            }}>
              <p style={{ margin: 0, color: '#065f46', fontSize: '14px', fontWeight: '500' }}>
                ✅ Tee times sent successfully!
              </p>
            </div>
          )}

          {sendStatus === 'error' && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
            }}>
              <p style={{ margin: 0, color: '#991b1b', fontSize: '14px', fontWeight: '500' }}>
                ❌ Failed to send tee times. Please try again.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowSelection(false)}
              disabled={isSending}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isSending ? 'not-allowed' : 'pointer',
                opacity: isSending ? 0.5 : 1,
              }}
            >
              ← Back
            </button>
            <button
              onClick={handleSendSelected}
              disabled={isSending || selectedTeeTimeIds.length === 0}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: (isSending || selectedTeeTimeIds.length === 0) ? '#9ca3af' : '#2d6a4f',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (isSending || selectedTeeTimeIds.length === 0) ? 'not-allowed' : 'pointer',
              }}
            >
              {isSending ? 'Sending...' : `Send ${selectedTeeTimeIds.length} Selected`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main view (initial three-button screen)
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={handleSkip}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '32px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            ⛳
          </div>
          <h2 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: 'bold',
            color: '#1f2937'
          }}>
            Player Created Successfully!
          </h2>
        </div>

        {/* Body */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ 
            margin: '0 0 16px 0', 
            color: '#4b5563',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            <strong>{newPlayer.name}</strong> has been added to your player database.
          </p>
          
          {isLoadingTeeTimes ? (
            <p style={{ 
              margin: 0, 
              color: '#6b7280',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Loading upcoming tee times...
            </p>
          ) : upcomingTeeTimes.length > 0 ? (
            <p style={{ 
              margin: 0, 
              color: '#6b7280',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              There {upcomingTeeTimes.length === 1 ? 'is' : 'are'} <strong>{upcomingTeeTimes.length} upcoming tee time{upcomingTeeTimes.length !== 1 ? 's' : ''}</strong>. Would you like to add them to {newPlayer.name}'s Player Portal?
            </p>
          ) : (
            <p style={{ 
              margin: 0, 
              color: '#9ca3af',
              fontSize: '14px',
              lineHeight: '1.5',
              fontStyle: 'italic'
            }}>
              No upcoming tee times available.
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '12px',
        }}>
          <button
            onClick={handleSendAll}
            disabled={isLoadingTeeTimes || upcomingTeeTimes.length === 0}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: (isLoadingTeeTimes || upcomingTeeTimes.length === 0) ? '#9ca3af' : '#2d6a4f',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (isLoadingTeeTimes || upcomingTeeTimes.length === 0) ? 'not-allowed' : 'pointer',
            }}
          >
            📧 Send {upcomingTeeTimes.length} Tee Time{upcomingTeeTimes.length !== 1 ? 's' : ''}
          </button>
          
          <button
            onClick={handleSelectTeeTimes}
            disabled={isLoadingTeeTimes || upcomingTeeTimes.length === 0}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              border: '2px solid #2d6a4f',
              backgroundColor: 'white',
              color: '#2d6a4f',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (isLoadingTeeTimes || upcomingTeeTimes.length === 0) ? 'not-allowed' : 'pointer',
              opacity: (isLoadingTeeTimes || upcomingTeeTimes.length === 0) ? 0.5 : 1,
            }}
          >
            ✓ Select Specific Tee Times
          </button>
          
          <button
            onClick={handleSkip}
            style={{
              padding: '12px 20px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkInvitationModal;
